/* eslint-disable @shopify/jsx-no-hardcoded-content */
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'preact/hooks';

import {isMobileDevice} from 'utilities/dom';
import {AppState} from 'utilities/app-state';
import {useLocation} from 'utilities/location';
import {useI18n} from 'utilities/i18n';

import styles from './ZipInputForm.css';
import {LocationPin, Close} from './media';

export interface ZipInputFormProps {
  focusOnMount?: boolean;
  isLoading: boolean;
}

export function ZipInputForm({isLoading, focusOnMount}: ZipInputFormProps) {
  const {putPostalCode, hasPostalCodeError, postalCode, resetPostalCodeError} =
    useLocation();
  const isMobile = isMobileDevice();
  const {translate: t} = useI18n();

  const [inputValue, setInputValue] = useState(postalCode);
  const inputRef = useRef<HTMLInputElement>(null);
  const {zipFormVisible} = useContext(AppState);

  const closeCallback = useCallback(() => {
    zipFormVisible.value = false;
  }, [zipFormVisible]);

  const onZipInputUpdate = useCallback(
    (newPostalCode: string | null) => {
      if (putPostalCode(newPostalCode)) {
        closeCallback();
      }
    },
    [putPostalCode, closeCallback],
  );

  const handleZipFormSubmitClick = useCallback(
    () => onZipInputUpdate(inputValue),
    [inputValue, onZipInputUpdate],
  );

  const handleInputValue = useCallback(
    (event: Event) => setInputValue((event.target as HTMLInputElement)?.value),
    [],
  );

  const handleClearInput = useCallback(() => {
    setInputValue(null);
    inputRef.current?.focus();
  }, []);

  const triggerSubmitOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (['Enter'].includes(event.key)) {
        setInputValue((event.target as HTMLInputElement)?.value);
        onZipInputUpdate((event.target as HTMLInputElement)?.value);
      }
    },
    [onZipInputUpdate],
  );

  useLayoutEffect(() => {
    if (focusOnMount) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [focusOnMount]);

  useEffect(() => {
    return zipFormVisible.subscribe((value) => {
      if (value) {
        setInputValue(postalCode);
      }
    });
  }, [zipFormVisible, postalCode]);

  useEffect(() => {
    if (isLoading) {
      closeCallback();
    }
  }, [isLoading, closeCallback]);

  useEffect(() => {
    return zipFormVisible.subscribe((value) => {
      if (!value && hasPostalCodeError) {
        resetPostalCodeError();
      }
    });
  }, [hasPostalCodeError, resetPostalCodeError, zipFormVisible]);

  const inputClassName = isMobile ? styles.mobile : '';

  const postalCodeError = hasPostalCodeError ? t('zipForm.error') : null;

  return (
    <div className={styles.locationWrapper}>
      <div
        className={[
          styles.locationEdit2,
          postalCodeError ? styles.HasError : '',
        ].join(' ')}
      >
        <div className={styles.inputWrapper}>
          <input
            id="postal-code__input"
            className={inputClassName}
            type="text"
            pattern="[0-9]*"
            inputMode="numeric"
            placeholder={t('zipForm.inputPlaceholder')}
            aria-label={t('zipForm.inputLabel')}
            autoComplete=""
            maxLength={5}
            value={inputValue ?? ''}
            onChange={handleInputValue}
            onKeyDown={triggerSubmitOnEnter}
            data-testid="input"
            ref={inputRef}
          />
          <div className={styles.leftIconWrapper}>
            <img src={LocationPin} alt={t('zipForm.locationPinAlt')} />
          </div>
          {postalCode && (
            <div className={styles.rightIconWrapper}>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClearInput}
                data-testid="clearInputButton"
              >
                <img src={Close} alt={t('zipForm.close')} />
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.submitButton}
          onClick={handleZipFormSubmitClick}
          data-testid="submitButton"
        >
          Update
        </button>
      </div>
      <div data-test="errorMessage" className={styles.errorMessage}>
        {postalCodeError}
      </div>
    </div>
  );
}
